import { z } from 'zod'

// ---------------------------------------------------------------------------
// User
// ---------------------------------------------------------------------------

export const RegisterSchema = z.object({
  nickname: z.string().min(1).max(50),
  avatar_emoji: z.string().min(1).max(4), // single emoji
  password: z.string().min(8).max(100),
})

export const LoginSchema = z.object({
  nickname: z.string().min(1).max(50),
  password: z.string().min(8).max(100),
})

export const UserSchema = z.object({
  id: z.string(),
  couple_id: z.string().nullable(),
  nickname: z.string(),
  avatar_emoji: z.string(),
  created_at: z.number(),
})

// ---------------------------------------------------------------------------
// Couple
// ---------------------------------------------------------------------------

export const CreateCoupleSchema = z.object({
  anniversary_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  couple_name: z.string().max(100).optional(),
})

export const JoinCoupleSchema = z.object({
  invite_code: z.string().length(6),
})

export const UpdateCoupleSchema = z.object({
  anniversary_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  couple_name: z.string().max(100).optional(),
  distance_mode: z.boolean().optional(),
})

export const CoupleSchema = z.object({
  id: z.string(),
  creator_id: z.string(),
  invite_code: z.string(),
  anniversary_date: z.string().nullable(),
  couple_name: z.string().nullable(),
  distance_mode: z.boolean(),
  created_at: z.number(),
})

// ---------------------------------------------------------------------------
// Note
// ---------------------------------------------------------------------------

export const MoodSchema = z.enum(['lovey', 'celebratory', 'dreamy', 'cozy'])
export const EnvelopeStyleSchema = z.enum(['plain', 'heart', 'flower'])

export const CreateNoteSchema = z.object({
  content: z.string().max(2000).optional(),
  mood: MoodSchema.optional(),
  envelope_style: EnvelopeStyleSchema.default('plain'),
  unlock_at: z.number().int().positive().optional(), // Unix timestamp
  doodle_encrypted_key: z.string().optional(), // encrypted R2 object key from POST /notes/doodle
})

export const NoteSchema = z.object({
  id: z.string(),
  couple_id: z.string(),
  sender_id: z.string(),
  content: z.string().nullable(),
  doodle_url: z.string().nullable(),
  mood: MoodSchema.nullable(),
  envelope_style: EnvelopeStyleSchema,
  unlock_at: z.number().nullable(),
  opened_at: z.number().nullable(),
  archived_by_sender: z.boolean(),
  archived_by_receiver: z.boolean(),
  deleted_at: z.number().nullable(),
  created_at: z.number(),
})

export const NoteListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  filter: z.enum(['inbox', 'sent', 'archived']).default('inbox'),
})

// ---------------------------------------------------------------------------
// Milestone
// ---------------------------------------------------------------------------

export const CreateMilestoneSchema = z.object({
  title: z.string().min(1).max(200),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  note: z.string().max(1000).optional(),
})

export const UpdateMilestoneSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  note: z.string().max(1000).optional(),
})

export const MilestoneSchema = z.object({
  id: z.string(),
  couple_id: z.string(),
  created_by: z.string(),
  title: z.string(),
  date: z.string(),
  note: z.string().nullable(),
  photo_urls: z.string().array().nullable(),
  created_at: z.number(),
})

// ---------------------------------------------------------------------------
// Countdown
// ---------------------------------------------------------------------------

export const CreateCountdownSchema = z.object({
  title: z.string().min(1).max(200),
  target_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
})

export const CountdownSchema = z.object({
  id: z.string(),
  couple_id: z.string(),
  created_by: z.string(),
  title: z.string(),
  target_date: z.string(),
  created_at: z.number(),
})

// ---------------------------------------------------------------------------
// Push
// ---------------------------------------------------------------------------

export const PushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  keys: z.object({
    p256dh: z.string(),
    auth: z.string(),
  }),
})

// ---------------------------------------------------------------------------
// Wishlist
// ---------------------------------------------------------------------------

export const WishlistVibeEnum = z.enum(['wander', 'watch', 'taste', 'weekend', 'try', 'together'])
export type WishlistVibe = z.infer<typeof WishlistVibeEnum>

export const WishlistItemSchema = z.object({
  id: z.string(),
  coupleId: z.string(),
  vibe: WishlistVibeEnum,
  title: z.string(), // decrypted
  note: z.string().nullable(), // decrypted
  linkUrl: z.string().nullable(), // decrypted
  linkTitle: z.string().nullable(), // decrypted
  linkDomain: z.string().nullable(), // plain
  linkFavicon: z.string().nullable(), // plain
  isAddedByMe: z.boolean(), // derived: addedBy === currentUserId
  addedByNickname: z.string(), // "you" or partner nickname
  addedByAvatar: z.string(), // emoji character
  nudgedAt: z.number().nullable(),
  nudgedBy: z.string().nullable(),
  doneAt: z.number().nullable(),
  isDoneByMe: z.boolean(), // derived: doneBy === currentUserId
  doneByNickname: z.string().nullable(), // "you" or partner nickname
  createdAt: z.number(),
})

export type WishlistItem = z.infer<typeof WishlistItemSchema>

export const WishlistCreateSchema = z.object({
  vibe: WishlistVibeEnum,
  title: z.string().min(1).max(300),
  note: z.string().max(1000).optional(),
  linkUrl: z.string().url().optional(),
})

export const WishlistUpdateSchema = z.object({
  title: z.string().min(1).max(300).optional(),
  note: z.string().max(1000).nullable().optional(),
  linkUrl: z.string().url().nullable().optional(),
})

// ---------------------------------------------------------------------------
// API response wrappers
// ---------------------------------------------------------------------------

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    data: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
  })

export const ErrorResponseSchema = z.object({
  error: z.string(),
})
